// Pattern Data Module - Data loading, CSV parsing, and calculations
// UPDATED: Non-repeating patterns always draw at full height, bottom-anchored and clipped
// FIXED: Moon patterns now always render at 15' height regardless of calculated panel length

// Global variables for data
let patterns = {};
let patternsLoaded = false;
let patternImage = null;
let imageLoaded = false;

// NEW: sessionStorage cache so shoppers browsing multiple product pages don't
// re-fetch and re-parse the same CSV on every single page load.
const WP_CALC_CACHE_KEY = 'wpCalcPatternsCSV_v1';
const WP_CALC_CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

function getCachedCSV() {
    try {
        const raw = sessionStorage.getItem(WP_CALC_CACHE_KEY);
        if (!raw) return null;
        const { text, timestamp } = JSON.parse(raw);
        if (Date.now() - timestamp > WP_CALC_CACHE_TTL_MS) return null;
        return text;
    } catch (e) {
        return null; // sessionStorage unavailable (e.g. private browsing) — just skip caching
    }
}

function setCachedCSV(text) {
    try {
        sessionStorage.setItem(WP_CALC_CACHE_KEY, JSON.stringify({ text, timestamp: Date.now() }));
    } catch (e) {
        // Storage full or unavailable — non-fatal, just means no caching this session
    }
}

// Load patterns from CSV file
async function loadPatternsFromCSV() {
    try {
        let csvText = getCachedCSV();

        if (csvText) {
            console.log('📊 Loading patterns from session cache...');
        } else {
            console.log('📊 Loading patterns from CSV (network)...');

            const response = await fetch(CONFIG.data.patternsCSV);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            csvText = await response.text();
            setCachedCSV(csvText);
        }

        console.log('📄 CSV ready, parsing...');
        
        // Parse CSV using Papa Parse
        const parsed = Papa.parse(csvText, {
            header: true,
            dynamicTyping: true,
            skipEmptyLines: true,
            transformHeader: (header) => header.trim().toLowerCase().replace(/\s+/g, '_')
        });
        
        if (parsed.errors.length > 0) {
            console.warn('⚠️ CSV parsing warnings:', parsed.errors);
        }
        
        console.log('📋 CSV parsed successfully:', parsed.data.length, 'patterns found');
        
        // Convert CSV data to pattern objects
        patterns = {};
        parsed.data.forEach((row, index) => {
            try {
                const pattern = createPatternFromCSV(row);
                if (pattern) {
                    patterns[pattern.id] = pattern;
                }
            } catch (error) {
                console.error(`❌ Error processing pattern at row ${index + 1}:`, error, row);
            }
        });
        
        patternsLoaded = true;
        console.log(`🎉 Total patterns loaded: ${Object.keys(patterns).length}`);
        return patterns;
        
    } catch (error) {
        console.error('❌ Error loading patterns from CSV:', error);
        
        // Fall back to demo pattern
        patterns = {
            'demo-pattern': {
                id: 'demo-pattern',
                name: 'Demo Pattern',
                sku: 'DEMO-001',
                repeatWidth: 108,
                repeatHeight: 144,
                hasRepeatHeight: true,
                panelWidth: 54,
                availableLengths: [9, 12, 15],
                minOverage: 4,
                saleType: 'panel',
                panelSequence: 'AB',
                sequenceLength: 2,
                imageUrl: '',
                thumbnailUrl: '',
                product_tearsheet_url: '',
                product_page_url: '',
                product_360_url: '',
                patternMatch: 'straight'
            }
        };
        patternsLoaded = true;
        console.log('🔄 Using demo pattern as fallback');
        return patterns;
    }
}

// Create pattern object from CSV row
function createPatternFromCSV(row) {
    if (!row.pattern_name || !row.sku) {
        console.warn('⚠️ Skipping row with missing required fields:', row);
        return null;
    }
    
    const defaults = CONFIG.calculator.defaults;
    
    // Create pattern ID from SKU
    const patternId = row.sku.toLowerCase().replace(/[^a-zA-Z0-9]/g, '-');
    
    // Parse available lengths
    const availableLengthsStr = row.available_lengths_feet || '';
    const availableLengths = availableLengthsStr ? 
        availableLengthsStr.split(',').map(l => parseInt(l.trim())).filter(l => !isNaN(l)) :
        defaults.availableLengths;
    
    // Parse panel sequence
    const panelSequence = row.panel_sequence || (row.sale_type === 'yard' ? '' : 'AB');
    const sequenceLength = panelSequence.length;
    
    // FIXED: Handle repeat height - for non-repeating patterns, always use max available length
    const repeatHeightValue = row.repeat_height_inches;
    let repeatHeight = 144;
    let hasRepeatHeight = true;
    
    if (repeatHeightValue === 'none' || repeatHeightValue === 'None' || repeatHeightValue === 'NONE') {
        hasRepeatHeight = false;
        // CORRECTED: Always use the maximum available panel length for drawing height
        // This ensures Moon patterns are always drawn at 15' height regardless of calculated panel length
        const maxLength = Math.max(...availableLengths);
        repeatHeight = maxLength * 12; // Convert feet to inches
        
        console.log(`🌙 Non-repeating pattern detected: ${row.pattern_name}`);
        console.log(`   Will always draw at max height: ${maxLength}' (${repeatHeight}") - bottom-anchored and clipped`);
    } else {
        repeatHeight = parseFloat(repeatHeightValue) || 144;
    }
    
    // Determine image URL
    let imageUrl = '';
    let thumbnailUrl = '';
    
    if (row.repeat_url && row.repeat_url.trim()) {
        imageUrl = row.repeat_url.trim();
        thumbnailUrl = imageUrl;
    } else {
        const filename = row.sku + '.jpg';
        imageUrl = CONFIG.data.imageBaseUrl + filename;
        thumbnailUrl = imageUrl;
    }
    
    return {
        id: patternId,
        name: row.pattern_name,
        sku: row.sku,
        repeatWidth: parseFloat(row.repeat_width_inches) || 108,
        repeatHeight: repeatHeight, // FIXED: Always set to max available length for non-repeating
        hasRepeatHeight: hasRepeatHeight,
        minOverage: defaults.minOverage,
        imageUrl: imageUrl,
        thumbnailUrl: thumbnailUrl,
        saleType: row.sale_type || 'panel',
        panelWidth: parseFloat(row.material_width_inches) || 54,
        availableLengths: availableLengths,
        panelSequence: panelSequence,
        sequenceLength: sequenceLength,
        rollWidth: row.sale_type === 'yard' ? defaults.rollWidth : null,
        minYardOrder: row.sale_type === 'yard' ? (row.min_yard_order || defaults.minYardOrder) : null,
        patternMatch: row.pattern_match || 'straight',
        isPanelRoll: row.sale_type === 'panel' && parseFloat(row.material_width_inches) <= 30,
        handle: patternId,
        product_tearsheet_url: row.product_tearsheet_url || '',
        product_page_url: row.product_page_url || '',
        product_360_url: row['360_view_url'] || ''
    };
}

// Preload pattern images
function preloadPatternImage(pattern) {
    return new Promise((resolve, reject) => {
        if (!pattern.imageUrl) {
            console.warn('No image URL provided for pattern:', pattern.name);
            resolve(null);
            return;
        }
        
        const img = new Image();
        img.crossOrigin = 'anonymous';
        
        img.onload = function() {
            console.log('✅ Pattern image loaded successfully');
            patternImage = img;
            imageLoaded = true;
            resolve(img);
        };
        
        img.onerror = function() {
            console.error('⚠️ Failed to load pattern image');
            patternImage = null;
            imageLoaded = false;
            resolve(null);
        };
        
        img.src = pattern.imageUrl;
    });
}

// Calculate panel requirements - standard logic (no changes needed here)
function calculatePanelRequirements(pattern, wallWidth, wallHeight) {
    if (!pattern || !pattern.saleType) {
        console.error('Invalid pattern data');
        return {
            panelsNeeded: 1,
            panelLength: 10,
            totalWidth: 54,
            totalHeight: 120,
            saleType: 'panel'
        };
    }
    
    if (pattern.saleType === 'yard') {
        return calculateYardRequirements(pattern, wallWidth, wallHeight);
    }
    
    const totalWidth = wallWidth + pattern.minOverage;
    const totalHeight = wallHeight + pattern.minOverage;
    
    let panelsNeeded = Math.ceil(totalWidth / pattern.panelWidth);

    // For 2-panel rolls, always complete the last roll pair
    if (pattern.isPanelRoll && panelsNeeded % 2 !== 0) {
        panelsNeeded += 1;
    }

    // Find the appropriate panel length
    let panelLength = 0;
    for (let length of pattern.availableLengths) {
        if (length * 12 >= totalHeight) {
            panelLength = length;
            break;
        }
    }
    
    // If no available length covers the wall, use the maximum available length
    if (panelLength === 0) {
        panelLength = Math.max(...pattern.availableLengths);
        console.log(`ℹ️ Wall height ${totalHeight}" exceeds max available panel length, capping at ${panelLength}' panels`);
    }
    
    const rollsNeeded = pattern.isPanelRoll ? panelsNeeded / 2 : panelsNeeded;

    console.log(`📏 Panel calculation: ${panelsNeeded} panels → ${rollsNeeded} ${pattern.isPanelRoll ? 'rolls' : 'panels'} × ${panelLength}'`);
    if (!pattern.hasRepeatHeight) {
        console.log(`🌙 Non-repeating pattern: Will draw at full ${pattern.repeatHeight}" height, bottom-anchored`);
    }

    return {
        panelsNeeded,
        rollsNeeded,
        panelLength: panelLength,
        totalWidth: panelsNeeded * pattern.panelWidth,
        totalHeight: panelLength * 12,
        saleType: 'panel',
        isPanelRoll: pattern.isPanelRoll || false,
        patternMatch: pattern.patternMatch || 'straight'
    };
}

// Calculate yard requirements - UPDATED for multi-repeat half-drop support
function calculateYardRequirements(pattern, wallWidth, wallHeight) {
    const totalWidth = wallWidth + pattern.minOverage;
    const totalHeight = wallHeight + pattern.minOverage;
    
    const isHalfDrop = pattern.patternMatch && pattern.patternMatch.toLowerCase() === 'half drop';
    
    if (!pattern.repeatHeight || pattern.repeatHeight <= 0) {
        console.error('Invalid repeat height for yard calculation');
        return {
            panelsNeeded: 1,
            panelLength: 10,
            totalYardage: 5,
            totalWidth: pattern.panelWidth || 54,
            totalHeight: 120,
            saleType: 'yard',
            stripLengthInches: 120,
            patternMatch: pattern.patternMatch || 'straight'
        };
    }
    
    if (!pattern.panelWidth || pattern.panelWidth <= 0) {
        console.error('Invalid panel width for yard calculation');
        return {
            panelsNeeded: 1,
            panelLength: 10,
            totalYardage: 5,
            totalWidth: 54,
            totalHeight: 120,
            saleType: 'yard',
            stripLengthInches: 120,
            patternMatch: pattern.patternMatch || 'straight'
        };
    }
    
    const stripsNeeded = Math.ceil(totalWidth / pattern.panelWidth);
    const repeatsPerPanel = pattern.panelWidth / pattern.repeatWidth;
    
    // Enhanced logging for half-drop yard calculations
    if (isHalfDrop) {
        console.log(`🔄 HALF-DROP YARD CALCULATION for ${pattern.name}:`, {
            repeatsPerPanel: repeatsPerPanel,
            isMultiRepeat: repeatsPerPanel >= 2,
            totalWidth: totalWidth,
            totalHeight: totalHeight,
            stripsNeeded: stripsNeeded
        });
    }
    
    let stripLengths = [];
    let maxStripLength = 0;
    
    if (isHalfDrop) {
        if (repeatsPerPanel >= 2) {
            // UPDATED: Multi-repeat half-drop patterns
            const repeatsNeeded = Math.ceil(totalHeight / pattern.repeatHeight);
            const stripLengthInches = Math.ceil(repeatsNeeded * pattern.repeatHeight);

            console.log(`📐 Multi-repeat half-drop calculation:`, {
                repeatsNeeded: repeatsNeeded,
                stripLengthInches: stripLengthInches,
                patternHeight: pattern.repeatHeight
            });

            for (let i = 0; i < stripsNeeded; i++) {
                stripLengths.push(stripLengthInches);
            }
            maxStripLength = stripLengthInches;
        } else {
            // EXISTING: Single-repeat half-drop patterns (every other panel offset)
            const baseHeight = totalHeight;
            const withOffset = baseHeight + (pattern.repeatHeight / 2);
            const repeatsNeeded = Math.ceil(withOffset / pattern.repeatHeight);
            const stripLengthInches = Math.ceil(repeatsNeeded * pattern.repeatHeight);

            console.log(`📐 Single-repeat half-drop calculation:`, {
                baseHeight: baseHeight,
                withOffset: withOffset,
                repeatsNeeded: repeatsNeeded,
                stripLengthInches: stripLengthInches
            });

            for (let i = 0; i < stripsNeeded; i++) {
                stripLengths.push(stripLengthInches);
            }
            maxStripLength = stripLengthInches;
        }
    } else {
        // Straight match patterns
        const repeatsNeeded = Math.ceil(totalHeight / pattern.repeatHeight);
        const stripLengthInches = Math.ceil(repeatsNeeded * pattern.repeatHeight);

        for (let i = 0; i < stripsNeeded; i++) {
            stripLengths.push(stripLengthInches);
        }
        maxStripLength = stripLengthInches;
    }

    const totalInches = stripLengths.reduce((sum, length) => sum + length, 0);
    const totalYardageRaw = totalInches / 36;

    // Extra yardage for single-repeat half-drop patterns
    let extraYardage = 0;
    if (isHalfDrop && repeatsPerPanel <= 1) {
        extraYardage = 1;
    }

    const totalYardage = Math.max(Math.ceil(totalYardageRaw + extraYardage), pattern.minYardOrder || 5);

    // Calculate rolls based on how many complete strips fit per roll
    const rollLengthInches = 11 * 36; // 396 inches per roll
    const stripsPerRoll = Math.max(1, Math.floor(rollLengthInches / maxStripLength));
    const rollsNeeded = Math.ceil(stripsNeeded / stripsPerRoll);

    console.log(`✅ YARD CALCULATION COMPLETE:`, {
        totalYardageRaw: totalYardageRaw,
        extraYardage: extraYardage,
        finalTotalYardage: totalYardage,
        maxStripLength: maxStripLength,
        rollLengthInches: rollLengthInches,
        stripsPerRoll: stripsPerRoll,
        stripsNeeded: stripsNeeded,
        rollsNeeded: rollsNeeded
    });

    return {
        panelsNeeded: stripsNeeded,
        rollsNeeded: rollsNeeded,
        panelLength: Math.floor(maxStripLength / 12),
        panelLengthInches: maxStripLength % 12,
        totalYardage: totalYardage,
        totalWidth: stripsNeeded * pattern.panelWidth,
        totalHeight: maxStripLength,
        saleType: 'yard',
        stripLengthInches: maxStripLength,
        stripLengths: stripLengths,
        patternMatch: pattern.patternMatch || 'straight',
        isHalfDrop: isHalfDrop,
        repeatsPerPanel: repeatsPerPanel
    };
}

// NEW: Find a loaded pattern's internal ID by its SKU (case-insensitive, trimmed).
// Used by the product-page bootstrap to auto-select the pattern matching the
// current Shopify product, instead of a manual dropdown.
function findPatternIdBySku(sku) {
    if (!sku) return null;
    const normalized = String(sku).trim().toLowerCase();
    for (const id in patterns) {
        if (patterns[id].sku && String(patterns[id].sku).trim().toLowerCase() === normalized) {
            return id;
        }
    }
    return null;
}

// Export data access functions to global scope
window.patternDataAPI = {
    patterns,
    patternImage,
    imageLoaded,
    loadPatternsFromCSV,
    preloadPatternImage,
    calculatePanelRequirements,
    calculateYardRequirements,
    findPatternIdBySku
};
